import { cdmApi } from '.';
import {
    TCluster,
    TClusterPayload,
    TProductClusterPayload,
    TShopCluster,
    TShopClusterPayload,
} from 'pages/ClusterManagement/types';

const CLUSTER_URL = '/cluster-management';
const CLUSTER_PREFIX = `${CLUSTER_URL}/cluster`;
const SHOP_CLUSTER_PREFIX = `${CLUSTER_URL}/shop`;
const PRODUCT_CLUSTER_PREFIX = `${CLUSTER_URL}/product`;

export type TClusterError = { error: { data: { message: string } & Record<string, any> } };
export type TClusterSuccess = { success: boolean; data: TCluster; message: string };

const clusterApi = cdmApi.injectEndpoints({
    endpoints: (builder) => ({
        // ----- CLUSTER -----
        getCluster: builder.query<{ data: TCluster[] }, void>({
            query: (params) => ({ url: `${CLUSTER_PREFIX}/`, method: 'get', params }),
            providesTags: ['Cluster'],
        }),
        addCluster: builder.mutation<TClusterSuccess, TClusterPayload>({
            query: (data) => ({ url: `${CLUSTER_PREFIX}/`, method: 'post', data }),
            invalidatesTags: ['Cluster'],
        }),
        editCluster: builder.mutation<TClusterSuccess, TCluster>({
            query: (data) => ({ url: `${CLUSTER_PREFIX}/${data._id}`, method: 'patch', data }),
            invalidatesTags: ['Cluster', 'ShopCluster', 'ProductCluster'],
        }),
        deleteCluster: builder.mutation<TClusterSuccess, TCluster>({
            query: (data) => ({ url: `${CLUSTER_PREFIX}/${data._id}`, method: 'delete' }),
            invalidatesTags: ['Cluster', 'ShopCluster', 'ProductCluster'],
        }),
        // ----- SHOP CLUSTER -----
        getShopCluster: builder.query<{ data: TShopCluster[] }, { retailerIds: (string | number)[] }>({
            query: (params) => ({ url: `${SHOP_CLUSTER_PREFIX}/`, method: 'get', params }),
            providesTags: ['ShopCluster'],
        }),
        editShopCluster: builder.mutation<{ success: boolean; data: TCluster }, TShopClusterPayload[]>({
            query: (data) => ({ url: `${SHOP_CLUSTER_PREFIX}/`, method: 'patch', data }),
            invalidatesTags: ['ShopCluster', 'Cluster'],
        }),
        deleteShopCluster: builder.mutation<{ success: boolean }, number>({
            query: (data) => ({ url: `${SHOP_CLUSTER_PREFIX}/${data}`, method: 'delete' }),
            invalidatesTags: ['ShopCluster'],
        }),
        // ----- PRODUCT CLUSTER -----
        getProductCluster: builder.query<{ data: TShopCluster[] }, void>({
            query: (params) => ({ url: `${PRODUCT_CLUSTER_PREFIX}/`, method: 'get' }),
            providesTags: ['ProductCluster'],
        }),
        editProductCluster: builder.mutation<{ success: boolean; data: TCluster }, TProductClusterPayload[]>({
            query: (data) => ({ url: `${PRODUCT_CLUSTER_PREFIX}/`, method: 'patch', data }),
            invalidatesTags: ['ProductCluster', 'Cluster'],
        }),
        deleteProductCluster: builder.mutation<{ success: boolean }, string>({
            query: (data) => ({ url: `${PRODUCT_CLUSTER_PREFIX}/${data}`, method: 'delete' }),
            invalidatesTags: ['ProductCluster'],
        }),
        // ----- EXPORT/UPLOAD -----
        exportCluster: builder.query<
            any,
            {
                type: 'cluster' | 'shop-cluster' | 'product-cluster';
                retailers?: (number | string)[];
            }
        >({
            query: (params) => ({
                url: `${CLUSTER_URL}/export`,
                method: 'get',
                params,
                responseType: 'arraybuffer',
            }),
        }),
        uploadCluster: builder.mutation<any, FormData>({
            query: (data) => ({
                url: `${CLUSTER_URL}/upload`,
                method: 'post',
                data,
            }),
            invalidatesTags: ['Cluster', 'ProductCluster', 'ShopCluster'],
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetClusterQuery,
    useAddClusterMutation,
    useEditClusterMutation,
    useDeleteClusterMutation,

    useLazyGetShopClusterQuery,
    useEditShopClusterMutation,
    useDeleteShopClusterMutation,

    useGetProductClusterQuery,
    useEditProductClusterMutation,
    useDeleteProductClusterMutation,

    useLazyExportClusterQuery,
    useUploadClusterMutation,
} = clusterApi;
